import React, { useState } from 'react';
import { Menu, X, Phone, Mail, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useCurrency, currencies } from '../contexts/CurrencyContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { selectedCurrency, changeCurrency } = useCurrency();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Destinations', href: '/destinations' },
    { name: 'Services', href: '/services' },
    { name: 'Why TRIPTO', href: '/why-us' },
    { name: 'About', href: '/about' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
      {/* Top contact bar */}
      <div className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Phone size={14} />
                <span>+91 7400488233</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={14} />
                <span>sales@triptotravels.org</span>
              </div>
            </div>
            <div className="hidden md:block">
              <span className="font-medium">🌟 Free Consultation - Plan Your Dream Trip Today!</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_715bdeb6-1ac6-412a-a604-4e6390e978d0/artifacts/kihnkqca_file.svg" 
              alt="TRIPTO" 
              className="h-10 w-auto"
            />
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-gray-700 hover:text-[#6C30ED] font-medium transition-colors duration-200 relative group"
              >
                {item.name}
                <span className="absolute inset-x-0 -bottom-1 h-0.5 bg-[#6C30ED] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200"></span>
              </a>
            ))}
          </nav>

          {/* Currency Selector & CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Currency Selector */}
            <div className="flex items-center space-x-2">
              <Globe size={18} className="text-gray-600" />
              <Select value={selectedCurrency} onValueChange={changeCurrency}>
                <SelectTrigger className="w-20 h-8 border-gray-300 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(currencies).map(([code, currency]) => (
                    <SelectItem key={code} value={code}>
                      <div className="flex items-center space-x-2">
                        <span>{currency.symbol}</span>
                        <span>{code}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              className="bg-gradient-to-r from-[#00DF98] to-[#4E8EFF] hover:from-[#00c687] hover:to-[#3d7eff] text-white font-semibold px-6 py-2 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Plan Your Trip
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-[#6C30ED] transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <nav className="space-y-4">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block text-gray-700 hover:text-[#6C30ED] font-medium transition-colors py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              {/* Mobile Currency Selector */}
              <div className="flex items-center space-x-2 mt-4">
                <Globe size={16} className="text-gray-600" />
                <Select value={selectedCurrency} onValueChange={changeCurrency}>
                  <SelectTrigger className="w-24 h-8 border-gray-300 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(currencies).map(([code, currency]) => (
                      <SelectItem key={code} value={code}>
                        <div className="flex items-center space-x-2">
                          <span>{currency.symbol}</span>
                          <span>{code}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-[#00DF98] to-[#4E8EFF] hover:from-[#00c687] hover:to-[#3d7eff] text-white font-semibold mt-4"
              >
                Plan Your Trip
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;